import React, { useState, forwardRef, memo } from "react";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Pagination from "@mui/material/Pagination";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Trans, useTranslation, type TranslatedString } from "@i18n";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";

interface PagerComponentProps {
  pageSize: number;
  currentPage: number;
  totalItemCount: number;
  setCurrentPage: (page: number) => void;
  setPageSize: (size: number) => void;
  totalPages: number;
  allowPagination?: boolean;
  selectedItems?: number;
  currentLoadedCount?: number;
  infiniteScroll?: boolean;
  gridOptions?: { enableCheckboxSelector?: boolean };
  isSelectAllVisible?: boolean;
  onSelectAllRecords?: () => void;
  /**
   * Noun the footer counts in — "52 rows", "52 folders". Grids whose rows are not generic
   * records pass their own already-translated word so every footer variant (total, selection,
   * partial load, select-all, paginated summary) names the same thing.
   */
  rowUnitLabel?: TranslatedString;
  totalRowCount?: number;
}

const PagerComponentBase = forwardRef(
  (
    {
      pageSize,
      currentPage,
      totalItemCount,
      setCurrentPage,
      setPageSize,
      totalPages,
      allowPagination,
      selectedItems,
      currentLoadedCount,
      infiniteScroll,
      gridOptions,
      isSelectAllVisible,
      onSelectAllRecords,
      rowUnitLabel,
      totalRowCount,
    }: PagerComponentProps,
    _ref,
  ) => {
    const { t } = useTranslation();
    const displayCount = totalRowCount ?? totalItemCount;
    const handlePageChange = (_: React.ChangeEvent<unknown>, newPage = 1) => {
      setCurrentPage(newPage);
    };

    const handlePageSizeChange = (newPageSize: number) => {
      setPageSize(newPageSize);
    };

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    const handleMenuItemClick = (value: number) => {
      setAnchorEl(null);
      handlePageSizeChange(value);
    };

    const customFooter = () => {
      const unit = rowUnitLabel ?? t("rows");
      const allLoaded =
        !infiniteScroll ||
        (currentLoadedCount !== undefined && totalItemCount > 0 && currentLoadedCount >= totalItemCount);
      const hasSelection = gridOptions?.enableCheckboxSelector && (selectedItems ?? 0) > 0;

      const elements: React.ReactElement[] = [];
      if (infiniteScroll && !allLoaded) {
        {
          hasSelection &&
            elements.push(
              <span>
                <Trans
                  i18nKey="grid_selectedText_when_partial_load"
                  values={{ count: selectedItems }}
                  components={{ bold: <strong /> }}
                />
              </span>,
            );
        }
        elements.push(
          <span>
            <Trans
              i18nKey="grid_partial_load_rows"
              values={{ cuurentLoaded: currentLoadedCount, total: displayCount, unit }}
              components={{ bold: <strong /> }}
            />
          </span>,
        );
        {
          isSelectAllVisible &&
            onSelectAllRecords &&
            elements.push(
              <ButtonComponent
                onClick={onSelectAllRecords}
                className="text-[var(--primary-color,#1976d2)] cursor-pointer bg-transparent border-none p-0"
                buttonType={BUTTON_TYPES.TEXT_BUTTON}
              >
                {t("Select_All_Rows", { count: displayCount, unit })}
              </ButtonComponent>,
            );
        }
      } else {
        {
          hasSelection
            ? elements.push(
                <span>
                  <Trans
                    i18nKey="grid_selectedText"
                    values={{ selected: selectedItems, total: displayCount, unit }}
                    components={{ bold: <strong /> }}
                  />
                </span>,
              )
            : elements.push(
                <span>
                  <Trans
                    i18nKey="grid_rows_text"
                    values={{ count: displayCount, unit }}
                    components={{ bold: <strong /> }}
                  />
                </span>,
              );
        }
      }

      return (
        <div className="flex justify-center items-center gap-2 text-sm text-black">
          {elements.map((element, index) => {
            return (
              <>
                {element}
                {index != elements.length - 1 && <span>|</span>}
              </>
            );
          })}
        </div>
      );
    };

    return (
      <>
        {allowPagination && (
          <div className="flex flex-row w-full items-center mt-3 mb-5 text-sm text-black">
            <Pagination
              count={totalPages}
              showFirstButton
              page={currentPage}
              showLastButton
              onChange={(event, pageNumber) => handlePageChange(event, pageNumber)}
            />
            <Button
              sx={{
                border: "1px solid #767676",
                justifyContent: "space-between",
                color: "black",
                borderRadius: "0",
              }}
              className="ms-2"
              aria-controls={open ? "page-size-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={open ? "true" : undefined}
              onClick={handleClick}
            >
              <div className="flex items-center justify-between w-full">
                <span>{pageSize}</span>
                <ExpandMoreIcon style={{ color: "gray" }} />
              </div>
            </Button>
            <Menu
              id="page-size-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              anchorOrigin={{
                vertical: "top",
                horizontal: "center",
              }}
              transformOrigin={{
                vertical: "bottom",
                horizontal: "center",
              }}
              MenuListProps={{
                style: {
                  paddingTop: "0.625rem",
                  paddingBottom: "0.625rem",
                },
              }}
            >
              {(["All", 5, 10, 15, 20] as (string | number)[]).map((size) => (
                <MenuItem
                  key={size}
                  onClick={() => handleMenuItemClick(size === "All" ? totalItemCount : (size as number))}
                  selected={size === (pageSize === 0 ? "All" : pageSize)}
                >
                  {size}
                </MenuItem>
              ))}
            </Menu>
            <span className="ms-2 me-2">{t("items per page")}</span>
            <div className="flex-1" />
            <div className="flex items-center ms-6">
              <span className="font-bold">{currentPage}</span>
              <span className="mx-1">{t("of")}</span>
              <span className="font-bold">{totalPages}</span>
              <span className="mx-1">{t("Pages")}</span>
              <span className="mx-1">
                (<span className="font-bold">{displayCount}</span> {rowUnitLabel ?? t("items")})
              </span>
            </div>
          </div>
        )}
        {!allowPagination && customFooter()}
      </>
    );
  },
);

PagerComponentBase.displayName = "PagerComponentBase";

const PagerComponent = memo(PagerComponentBase);

export default PagerComponent;
